import React from 'react';
import { Box, Dialog, IconButton, makeStyles, Typography } from '@material-ui/core';
import moment from 'moment';
import { CloseIcon } from '../../../../components/Icon';
import { baseCssBooking } from '../../components/PopWindows/baseCss';
import admissionUtil from '../../../../utils/admissionUtil';
import {
  formatTimestamp,
  getAttention,
  getLsolationPrecaution,
  isNotEmptyArray,
} from '../../../../utils/newBedStatusUtil';
import { PatientUI } from '../../components/InternalBooking/TransferInTable/Row';
import { BedStatusIcon } from '../../components';
import { i18n } from '../../../../language';
import Text from '../../../../components/Text';
import MdroInfo from '../../../../components/MdroInfo';
import { bedP } from '../../bsApp';
import CmsAlert, { CMS_ALERT_ICON_VARIANT } from '../../../../components/CmsAlert';
import { PendingAssignSvgIcon, WarningIcon, EyeIcon } from './SvgIcon';

const useStyles = makeStyles(() => ({
  dialog: {
    '& .MuiPaper-root': {
      // width: '80%',
      // height: '100%',
      display: 'flex',
      justifyContent: 'center',
      background: 'transparent',
      boxShadow: 'unset',
    },
  },
  root: {
    width: '100%',
    maxWidth: '100rem',
    display: 'flex',
    borderRadius: '0.625rem',
    boxShadow: '0 -2px 21px 0 rgba(30, 56, 140, 0.18)',
    backgroundColor: '#fff',
    background: '#fff',
    height: '100%',
    overflow: 'auto',
    minWidth: '100%',
    flexDirection: 'column',
    padding: '1.857rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignContent: 'center',
    alignItems: 'center',
    gap: '2.857rem',
    // marginBottom: '1.2rem',
  },
  patientInfoContainer: ({ patGender }) => ({
    display: 'flex',
    padding: '0.857rem 1.214rem',
    background: patGender === 'F' ? '#FFEAF1' : '#F1F5FB',
    borderRadius: '1.142rem',
    minWidth: '33rem',
  }),
  eAdmInfoContainer: {
    display: 'flex',
    padding: '0.857rem 1.214rem',
    flexDirection: 'column',
    gap: '0.5rem',
    minWidth: '17.5rem',
  },
  admTimeBox: {
    background: '#34C759',
    color: '#FFF',
    padding: '6px 4px',
    borderRadius: '4px',
    marginLeft: '0.285rem',
  },
  eyeIconBox: {
    cursor: 'pointer',
    background: '#538fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3px',
    borderRadius: '6px',
  },
  isolationPrecaution: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#2e4469',
    margin: '1.25rem 0 0.375rem 0',
  },
  attentionItem: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    paddingTop: '0.5rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  attentionItemLabel: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    color: '#2e4469',
    paddingTop: '0.5rem',
    fontWeight: 'bold',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    marginRight: '0.5rem',
  },
}));

export default function PendingAssignPopup({ open, closePop, patientInfo, goToEAdmissionForm }) {
  const { caseNo = '', bedNo, eAdmInfo, patGender, age, patInfo } = patientInfo;
  const classes = useStyles({ patGender });
  const baseClasses = baseCssBooking();
  const patientName = admissionUtil.useGetName({
    chiName: patInfo?.chiName,
    engName: patInfo?.engName,
    loginFlag: true,
  });

  return (
    <Dialog open={open} maxWidth={false} className={classes.dialog}>
      <div className={classes.root}>
        <Box className={classes.header}>
          <IconButton className={baseClasses.closeIcon} onClick={closePop} style={{ marginRight: '-10px' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <main style={{ overflow: 'auto', flex: '1' }}>
          <Box display="flex" gridGap="1.142rem">
            <Box className={classes.patientInfoContainer} alignItems="center" gridGap="0.714rem">
              <Box display="flex" gridGap="1.428rem" alignItems="center">
                {bedNo ? (
                  <BedStatusIcon allData={patientInfo?.bedstatusInfo} disabled={true} />
                ) : (
                  <PendingAssignSvgIcon gender={patGender} size="2.2rem" />
                )}
                <PatientUI
                  iconBoxSize="2rem"
                  caseNo={caseNo}
                  bedNo={bedNo}
                  name={patientName}
                  gender={patGender}
                  age={age}
                />
              </Box>
            </Box>

            <Box className={classes.eAdmInfoContainer}>
              <Box fontSize="1.142rem" color="#538fff" fontWeight="bold">
                E-Adm.
              </Box>
              {eAdmInfo ? (
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                      <Box fontSize="1.142rem" color="#7D86A9" fontWeight="bold">
                        ADT
                      </Box>
                      <Box className={classes.admTimeBox}>{formatTimestamp(eAdmInfo?.adt) ?? ''}</Box>
                    </Box>
                    {Boolean(eAdmInfo?.priority) && (
                      <Box ml="1.5rem">
                        <WarningIcon warningColor="#951B07" width="2rem" height="1.647rem" />
                      </Box>
                    )}
                  </Box>
                  <Box display="flex" alignItems="center" mt="0.2rem" justifyContent="space-between">
                    <Box fontSize="1.142rem" color="#538fff">
                      {eAdmInfo?.caseNo ?? ''}
                    </Box>
                    <Box className={classes.eyeIconBox} onClick={goToEAdmissionForm}>
                      <EyeIcon size="20" color="#FFF" />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box fontSize="1rem">N/A</Box>
              )}
            </Box>
          </Box>

          <Box mt="1.5rem" display="flex" gridGap="1.25rem">
            <Box width="50%">
              <Typography className={classes.isolationPrecaution}>
                {i18n.t('BEDSTATUS_ISOLATION_PRECAUTION')}
              </Typography>
              {getLsolationPrecaution(classes, patientInfo)}
              {patientInfo?.covidPositivePat && (
                <>
                  <Box display="flex">
                    <Typography className={classes.attentionItemLabel}>{i18n.t('BEDSTATUS_REPORT_TIME')}</Typography>

                    <Typography className={classes.attentionItem}>
                      {/* COVID info: {allData.covidPositivePat?.[0].covidDescription} */}
                      {patientInfo?.covidPositivePat
                        ? moment(patientInfo?.covidPositivePat?.[0].reportDtm).format('DD MMMM YYYY HH:MM')
                        : ''}
                    </Typography>
                  </Box>
                  <Box display="flex">
                    <Typography className={classes.attentionItemLabel}>{i18n.t('BEDSTATUS_CT_VALUE')} </Typography>
                    <Typography className={classes.attentionItem}>
                      {patientInfo?.covidPositivePat?.[0].ctValue}
                    </Typography>
                  </Box>
                  <Box display="flex">
                    <Typography className={classes.attentionItem}>
                      {patientInfo?.covidPositivePat?.[0].covidDescription}
                    </Typography>
                  </Box>
                  <Box display="flex">
                    <Typography className={classes.attentionItemLabel}>{i18n.t('BEDSTATUS_COUNTER')}</Typography>
                    <Typography className={classes.attentionItem}>
                      {patientInfo?.covidPositivePat && (
                        <>
                          {Math.round(
                            (moment().valueOf() - patientInfo?.covidPositivePat?.[0].reportDtm) / (24 * 60 * 60 * 1000)
                          )}
                          {i18n.t('BEDSTATUS_DAY')}
                        </>
                      )}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
            <Box width="50%">
              <Typography className={classes.isolationPrecaution}>{i18n.t('BEDSTATUS_ATTENTION')}</Typography>
              <Box className={classes.attentionItem}>{getAttention(classes, patientInfo)}</Box>
            </Box>
          </Box>

          <Box marginTop="1.5rem">
            <Text color="#7d86a9" fontSize="1rem">
              MDRO Alert
            </Text>
            <Box display="flex" flexWrap="wrap" mt="0.62rem" gridGap="0.62rem">
              {isNotEmptyArray(patientInfo?.mdroInfo) || isNotEmptyArray(patientInfo?.[bedP?.cmsAlertInfos]) ? (
                <>
                  {patientInfo?.mdroInfo?.map((item) => (
                    <MdroInfo content={item.alertDesc} key={item.alertDesc} popValue={item} />
                  ))}
                  <CmsAlert alertsData={patientInfo?.[bedP?.cmsAlertInfos]} variant={CMS_ALERT_ICON_VARIANT.WIDE} />
                </>
              ) : (
                '--'
              )}
            </Box>
          </Box>
        </main>
      </div>
    </Dialog>
  );
}
